import Ternary from '@/components/Tenary';
import { CheckOutlined } from '@ant-design/icons';
import { useControllableValue } from 'ahooks';
import { Tag as AntTag, Select, SelectProps } from 'antd';
import { uniq, uniqWith } from 'lodash';
import { BaseSelectRef } from 'rc-select';
import { useEffect, useMemo, useRef, useState } from 'react';
import useExtractTags from './use-extract-tags';

export interface ExtractTagSelectProps {
  value?: string[];
  mode?: 'multiple';
  onChange?: (value: string[], tags: Tag[]) => void;
  defaultValue?: string[];
  onTagChange?: (tags?: Tag[]) => void;
  acceptedTypes?: ('text' | 'table' | 'seal' | 'hand_writing')[];
}

export interface Tag {
  value: string;
  label: string;
  type?: 'text' | 'table' | 'seal' | 'hand_writing';
  table_code?: string;
  table_name?: string;
}

export const ExtractTagSelect = (props: ExtractTagSelectProps) => {
  const ref = useRef<BaseSelectRef>(null);
  const [state, setState] = useControllableValue<string[]>(props);
  const [current, setCurrent] = useState<Tag[]>();
  const [open, setOpen] = useState(false);
  const { extractTags, selectOptions } = useExtractTags();
  const { acceptedTypes = ['text', 'table', 'seal', 'hand_writing'] } = props;

  //TODO useExtractTags 和这里重复了
  useEffect(() => {
    if (!extractTags) return setCurrent([]);

    const text_tags = extractTags?.text_tags
      .map((tag) => {
        if (state?.includes(tag.tag_code)) {
          return {
            value: tag.tag_code,
            label: tag.tag_name,
            type: 'text',
          };
        }
        return null;
      })
      .filter(Boolean);

    const table_tags = extractTags?.table_tags.reduce((prev, cur) => {
      cur.tags.forEach((tag) => {
        if (state?.includes(tag.tag_code)) {
          prev.push({
            value: tag.tag_code,
            label: tag.tag_name,
            table_code: cur.table_code,
            table_name: cur.table_name,
            type: 'table',
          });
        }
        return prev;
      });
      return prev;
    }, [] as Tag[]);

    const seal_tags =
      extractTags?.seal_tags
        ?.map((tag) => {
          if (state?.includes(tag.tag_code)) {
            return {
              value: tag.tag_code,
              label: tag.tag_name,
              type: 'seal',
            };
          }
          return null;
        })
        .filter(Boolean) || [];

    const hand_writing_tags =
      extractTags?.hand_writing_tags
        ?.map((tag) => {
          if (state?.includes(tag.tag_code)) {
            return {
              value: tag.tag_code,
              label: tag.tag_name,
              type: 'hand_writing',
            };
          }
          return null;
        })
        .filter(Boolean) || [];

    setCurrent([
      ...text_tags,
      ...table_tags,
      ...seal_tags,
      ...hand_writing_tags,
    ] as Tag[]);
  }, [extractTags, setCurrent, state]);

  const showTableTags = Boolean(
    extractTags?.table_tags?.length && acceptedTypes.includes('table'),
  );
  const showTextTags = Boolean(
    extractTags?.text_tags?.length && acceptedTypes.includes('text'),
  );
  const showSealTags = Boolean(
    extractTags?.seal_tags?.length && acceptedTypes.includes('seal'),
  );
  const showHandWritingTags = Boolean(
    extractTags?.hand_writing_tags?.length &&
      acceptedTypes.includes('hand_writing'),
  );

  const handleDeselect = (tag_code: string) => {
    const newCurrent = current?.filter((v) => v.value !== tag_code) || [];
    const newState = state?.filter((v) => v !== tag_code);
    setState(newState, newCurrent);
    setCurrent(newCurrent);
  };

  const handleChange = (tag: Tag) => {
    const isSelected = state?.includes(tag.value);
    const isMultiple = props.mode === 'multiple';

    if (isMultiple) {
      if (isSelected) {
        handleDeselect(tag.value);
      } else {
        const newCurrent = uniqWith(
          [...(current || []), tag],
          (a, b) => a.value === b.value,
        );

        const newState = uniq([...(state || []), tag.value]);
        setState(newState, newCurrent);
        setCurrent(newCurrent);
      }
    } else {
      const newState = isSelected ? [] : [tag.value];

      const newCurrent = isSelected ? [] : [tag];
      setState(newState, newCurrent);
      setOpen(false);
    }
  };

  const tagRender: SelectProps['tagRender'] = (props) => {
    const { label, value, closable, onClose } = props;
    const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
      event.preventDefault();
      event.stopPropagation();
    };
    return (
      <AntTag
        // color={value}
        onMouseDown={onPreventMouseDown}
        closable={false}
        // onClose={onClose}
        style={{ marginInlineEnd: 4 }}
      >
        {label}
      </AntTag>
    );
  };

  return (
    <Select
      ref={ref}
      value={state}
      open={open}
      onDropdownVisibleChange={setOpen}
      tagRender={tagRender}
      mode={props.mode}
      getPopupContainer={(el) => el.parentElement as HTMLElement}
      options={selectOptions}
      onDeselect={(value) => {
        handleDeselect(value);
      }}
      allowClear={false}
      className="[&_.modern-tag]:!bg-[#F2F6FC] w-full"
      dropdownRender={() => {
        return (
          <div className="p-2 max-h-[400px] overflow-y-auto">
            <Ternary condition={showTextTags}>
              <div className="ant-select-item-group">
                <span className="ant-select-item-group-title">抽取点</span>
                <div className="ant-select-item-group-list">
                  {extractTags?.text_tags.map((tag) => {
                    const isSelected = state?.some((id) => id === tag.tag_code);
                    return (
                      <div
                        className="hover:bg-[#F4F7FF] rounded ant-select-item"
                        key={tag.tag_code}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          handleChange({
                            value: tag.tag_code,
                            label: tag.tag_name,
                            type: 'text',
                          });
                        }}
                      >
                        {isSelected && (
                          <CheckOutlined className="mr-1 text-blue-400" />
                        )}
                        {tag.tag_name}
                      </div>
                    );
                  })}
                </div>
              </div>
            </Ternary>
            <Ternary condition={showTableTags}>
              <div className="ant-select-item-group">
                <span className="ant-select-item-group-title">表格抽取点</span>
                <div className="px-2 ant-select-item-group-list">
                  {extractTags?.table_tags.map((table) => {
                    return (
                      <div key={table.table_code}>
                        <div className="mt-1 ant-select-item-group-title">
                          {table.table_name}
                        </div>
                        {table?.tags.map((item) => {
                          const isSelected = state?.some(
                            (id) => id === item.tag_code,
                          );
                          return (
                            <div
                              className="hover:bg-[#F4F7FF] rounded ant-select-item"
                              key={item.tag_code}
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                handleChange({
                                  value: item.tag_code,
                                  label: item.tag_name,
                                  table_code: table.table_code,
                                  table_name: table.table_name,
                                  type: 'table',
                                });
                              }}
                            >
                              {isSelected && (
                                <CheckOutlined className="mr-1 text-blue-400" />
                              )}
                              {item.tag_name}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
            </Ternary>
            <Ternary condition={showSealTags}>
              <div className="ant-select-item-group">
                <span className="ant-select-item-group-title">印章抽取点</span>
                <div className="ant-select-item-group-list">
                  {extractTags?.seal_tags?.map((tag) => {
                    const isSelected = state?.some((id) => id === tag.tag_code);
                    return (
                      <div
                        className="hover:bg-[#F4F7FF] rounded ant-select-item"
                        key={tag.tag_code}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          handleChange({
                            value: tag.tag_code,
                            label: tag.tag_name,
                            type: 'seal',
                          });
                        }}
                      >
                        {isSelected && (
                          <CheckOutlined className="mr-1 text-blue-400" />
                        )}
                        {tag.tag_name}
                      </div>
                    );
                  })}
                </div>
              </div>
            </Ternary>
            <Ternary condition={showHandWritingTags}>
              <div className="ant-select-item-group">
                <span className="ant-select-item-group-title">
                  手写体抽取点
                </span>
                <div className="ant-select-item-group-list">
                  {extractTags?.hand_writing_tags?.map((tag) => {
                    const isSelected = state?.some((id) => id === tag.tag_code);
                    return (
                      <div
                        className="hover:bg-[#F4F7FF] rounded ant-select-item"
                        key={tag.tag_code}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          handleChange({
                            value: tag.tag_code,
                            label: tag.tag_name,
                            type: 'hand_writing',
                          });
                        }}
                      >
                        {isSelected && (
                          <CheckOutlined className="mr-1 text-blue-400" />
                        )}
                        {tag.tag_name}
                      </div>
                    );
                  })}
                </div>
              </div>
            </Ternary>
          </div>
        );
      }}
    />
  );
};

/** 字符串格式 */
export const ExtractTagSelectItem = (
  props: ExtractTagSelectProps & {
    onChange?: (value?: string, tag?: Tag) => void;
    value?: string;
  },
) => {
  const { onChange, value, ...rest } = props;
  return (
    <ExtractTagSelect
      onChange={(value, tags) => {
        onChange?.(value?.[0], tags?.[0]);
      }}
      value={value ? [value] : []}
      {...rest}
    />
  );
};
